import * as React from 'react';
import { CalendarViewMode } from './types';
import { getDaysInRange, getRangeTitle, getVisibleRange, shiftFocusedDate } from './utils/dateRange';

interface IOptions {
	/** Supply to control the view mode externally (e.g. URL-synced on the My Calendar page). */
	viewMode?: CalendarViewMode;
	onViewModeChange?: (mode: CalendarViewMode) => void;
	/** Supply to control the focused date externally. */
	focusedDate?: Date;
	onFocusedDateChange?: (date: Date) => void;
}

/**
 * Owns (or adapts) the calendar's view mode + focused date and derives the visible window, day
 * list, range title, and navigation handlers shared by every calendar surface. Pass state in to
 * control it (the My Calendar page URL-syncs via nuqs); omit it for self-contained local state
 * (the embedded board / action-item views).
 */
export function useCalendarNavigation(options?: IOptions) {
	const [internalViewMode, setInternalViewMode] = React.useState<CalendarViewMode>('month');
	const [internalFocusedDate, setInternalFocusedDate] = React.useState<Date>(() => new Date());

	const viewMode = options?.viewMode ?? internalViewMode;
	const focusedDate = options?.focusedDate ?? internalFocusedDate;
	const setViewMode = options?.onViewModeChange ?? setInternalViewMode;
	const setFocusedDate = options?.onFocusedDateChange ?? setInternalFocusedDate;

	const range = React.useMemo(() => getVisibleRange(viewMode, focusedDate), [viewMode, focusedDate]);
	const days = React.useMemo(() => getDaysInRange(range), [range]);
	const title = getRangeTitle(viewMode, focusedDate);

	const onPrevious = React.useCallback(
		() => setFocusedDate(shiftFocusedDate(viewMode, focusedDate, -1)),
		[viewMode, focusedDate, setFocusedDate]
	);
	const onNext = React.useCallback(
		() => setFocusedDate(shiftFocusedDate(viewMode, focusedDate, 1)),
		[viewMode, focusedDate, setFocusedDate]
	);
	const onToday = React.useCallback(() => setFocusedDate(new Date()), [setFocusedDate]);
	const onSelectDate = React.useCallback(
		(date: Date) => {
			setFocusedDate(date);
			setViewMode('day');
		},
		[setFocusedDate, setViewMode]
	);

	return {
		viewMode,
		focusedDate,
		range,
		days,
		title,
		onViewModeChange: setViewMode,
		onPrevious,
		onNext,
		onToday,
		onSelectDate,
	};
}

export type CalendarNavigation = ReturnType<typeof useCalendarNavigation>;
